import styles from './EncounterTemplateDetailsPanel.module.scss';
import { useGetEncounterTemplateQuery, useUpdateEncounterTemplateMutation } from '@app/medical-record/api';
import {
  EncounterTemplateNoteForm,
  EncounterTemplateNoteFormValue,
} from '@app/medical-record/widgets/EncounterTemplates.Details/EncounterTemplateNoteForm';
import { useCallback } from 'react';
import { notify, SHORTCUT } from '@app/ui';
import { Form } from '@webinex/antik';
import { useTranslation } from 'react-i18next';
import { Space } from 'antd';

export interface EncounterNoteDetailsPanelProps {
  templateId: string;
  onSubmitted?: () => void;
  readonly?: boolean;
  richTextAreaClassname?: string;
  actions?: React.ReactNode;
  autoFocusNote?: boolean;
}

function useSubmit(props: EncounterNoteDetailsPanelProps) {
  const { templateId, onSubmitted } = props;
  const [update] = useUpdateEncounterTemplateMutation();

  return useCallback(
    async (value: EncounterTemplateNoteFormValue) => {
      update({ id: templateId, note: value.note, name: value.name })
        .unwrap()
        .then(() => {
          notify.success.t('encounterTemplates.updated');
          onSubmitted && onSubmitted();
        });
    },
    [update, onSubmitted, templateId],
  );
}

export function EncounterTemplateDetailsPanel(props: EncounterNoteDetailsPanelProps) {
  const { templateId, readonly, richTextAreaClassname, actions, autoFocusNote } = props;
  const { t } = useTranslation();
  const { data: template } = useGetEncounterTemplateQuery({ id: templateId });
  const handleSubmit = useSubmit(props);

  if (!template) return null;

  return (
    <div className="flex flex-col h-full">
      <div className={styles.body}>
        <EncounterTemplateNoteForm
          value={{ name: template.name, note: template.note ?? null }}
          onSubmit={handleSubmit}
          readonly={readonly}
          className={richTextAreaClassname}
          autoFocusNote={autoFocusNote}
        />
      </div>
      <Space direction="horizontal" className="flex-none w-full justify-end">
        {actions}
        <Form.Submit data-shortcut={SHORTCUT.save} type="primary" uid="encounter-template-note-form">
          {t('save')}
        </Form.Submit>
      </Space>
    </div>
  );
}
